/**
 * EditorPage — DOCTRINE Document Editor
 *
 * Block-based rich text editor powered by Mina Rich Editor.
 * AI generation hooks route to /api/chat via doctrineChatProvider.
 * Export Markdown feeds directly into DOCTRINE pipeline steps.
 */

import { useRef } from 'react'
import {
  Editor,
  EditorProvider,
  createInitialState,
  createEmptyContent,
  useEditorAPI,
} from '@/components/ui/rich-editor'
import type { AIProvider, AIStreamOptions } from '@/components/ui/rich-editor/ai/types'

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3003'

/**
 * doctrineChatProvider — AIProvider implementation backed by DOCTRINE /api/chat.
 *
 * Flow:
 *   1. POST /api/chat/sessions to create a transient session
 *   2. POST /api/chat/sessions/:id/messages with SSE streaming
 *   3. Yield text chunks as they arrive; close session when done
 */
const doctrineChatProvider: AIProvider = {
  async *stream(prompt: string, options?: AIStreamOptions): AsyncIterable<string> {
    // Step 1 — create a transient chat session
    const sessionRes = await fetch(`${API_BASE}/api/chat/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: 'Editor AI',
        systemPrompt: options?.systemPrompt ?? 'You are a helpful writing assistant for a technical document editor. Respond with clear, professional prose.',
      }),
    })

    if (!sessionRes.ok) {
      const text = await sessionRes.text()
      throw new Error(`Failed to create chat session: ${sessionRes.status} ${text}`)
    }

    const { session } = await sessionRes.json() as { session: { id: string } }

    // Step 2 — send the prompt as an SSE-streaming message
    const msgRes = await fetch(`${API_BASE}/api/chat/sessions/${session.id}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content: prompt,
        model: options?.model,
      }),
    })

    if (!msgRes.ok || !msgRes.body) {
      throw new Error(`Chat request failed: ${msgRes.status}`)
    }

    // Step 3 — parse SSE and yield text chunks
    const reader = msgRes.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const raw = line.slice(6).trim()
        if (raw === '[DONE]') return

        try {
          const event = JSON.parse(raw) as Record<string, unknown>
          // DOCTRINE chat SSE emits { type: 'delta', content: '...' }
          if (event.type === 'delta' && typeof event.content === 'string') {
            yield event.content
          }
        } catch {
          // ignore malformed lines
        }
      }
    }
  },
}

// ─── Markdown export button ───────────────────────────────────────────────────

function ExportButton() {
  const api = useEditorAPI()

  const handleExport = () => {
    const markdown = api.getMarkdown()
    const blob = new Blob([markdown], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'document.md'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <button
      onClick={handleExport}
      className="px-4 py-2 text-sm rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 transition-colors"
    >
      Export Markdown
    </button>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const initialState = createInitialState({
  id: 'root',
  type: 'container',
  children: createEmptyContent(),
  attributes: {},
})

export function EditorPage() {
  const instanceRef = useRef<string>(`editor-${Date.now()}`)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6">
      <div className="max-w-4xl mx-auto">
        <EditorProvider
          key={instanceRef.current}
          initialState={initialState}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Document Editor
            </h1>
            <div className="flex gap-3">
              <ExportButton />
            </div>
          </div>

          {/* Editor surface */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 min-h-[600px]">
            <Editor
              aiProvider={doctrineChatProvider}
              aiSystemPrompt="You are a helpful writing assistant for DOCTRINE technical documents. Respond with clear, authoritative prose."
            />
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Markdown exported here feeds directly into DOCTRINE pipeline steps.
          </p>
        </EditorProvider>
      </div>
    </div>
  )
}
